import { ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const SIDE_CART_SELECTOR = 'cart-drawer-component.side-cart-drawer';

/**
 * @returns {string | null}
 */
function getSideCartSectionId() {
  const drawer = document.querySelector(SIDE_CART_SELECTOR);
  const cartItems = drawer?.querySelector('cart-items-component');

  if (!(cartItems instanceof HTMLElement)) return null;

  return cartItems.dataset.sectionId ?? null;
}

/**
 * Opens the side cart drawer if present.
 */
function openSideCart() {
  const drawer = document.querySelector(SIDE_CART_SELECTOR);

  if (!(drawer instanceof HTMLElement) || !('open' in drawer) || typeof drawer.open !== 'function') {
    return;
  }

  drawer.open();
}

/**
 * Closes the quick-add modal after adding from a product card.
 */
function closeQuickAddModal() {
  const quickAddDialog = document.getElementById('quick-add-dialog');

  if (
    quickAddDialog instanceof HTMLElement &&
    'closeDialog' in quickAddDialog &&
    typeof quickAddDialog.closeDialog === 'function'
  ) {
    quickAddDialog.closeDialog();
  }
}

/**
 * Refreshes the header/side-cart section so drawer contents match the live cart.
 * @param {Record<string, string> | undefined} sections
 */
async function refreshSideCart(sections) {
  const sectionId = getSideCartSectionId();
  if (!sectionId) return;

  try {
    if (sections?.[sectionId]) {
      await morphSection(sectionId, sections[sectionId]);
      return;
    }
  } catch (error) {
    console.error('[side-cart] Section morph failed, falling back to re-render', error);
  }

  // Always bypass cache so we never reopen the empty-state HTML from page load.
  await sectionRenderer.renderSection(sectionId, { cache: false });
}

/**
 * @param {Event} event
 */
async function handleCartUpdate(event) {
  // CartAddEvent extends Event (not CustomEvent) — check detail, not instanceof CustomEvent.
  const detail = /** @type {{ detail?: { data?: Record<string, any> } }} */ (event).detail;
  if (!detail?.data) return;

  const { source, didError, sections } = detail.data;

  if (didError || source !== 'product-form-component') return;

  closeQuickAddModal();

  await refreshSideCart(sections);
  openSideCart();
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
